import { AfterViewInit, Directive, ElementRef, HostListener, Input, OnInit, Renderer2 } from '@angular/core';
import { ContentImagePipe } from '@fgb/portal-component-library/src/lib/pipes';

/**
 * This directive is used to preload image and loads only when it's in the view, main purpose is to get initial page load as lightweight as possible.
 * @see {LazyLoadDirective} see lazyload directive too for more lazy load feature
 */
@Directive({
  selector: '[fgbImgPreload]',
})
export class ImgPreloadDirective implements OnInit, AfterViewInit {
  @Input() dataSrc: string | undefined;
  @Input() imgOptions: { variantId: any; loyaltyType?: string } = { variantId: '1', loyaltyType: undefined };
  private _src: string | undefined;
  private _contentImgPipe: ContentImagePipe = new ContentImagePipe();
  @HostListener('load') onLoad() {
    const image = this._el.nativeElement;
    const parent = image.parentElement;

    if (image && parent) {
      const imgRect = image.getBoundingClientRect();
      const parentRect = parent.getBoundingClientRect();

      const fullyVisible =
        imgRect.top >= parentRect.top &&
        imgRect.left >= parentRect.left &&
        imgRect.bottom <= parentRect.bottom &&
        imgRect.right <= parentRect.right;

      if (fullyVisible) {
        // Remove skeleton if image is fully visible
        this.renderer.removeClass(this._el.nativeElement.parentElement, 'skeleton');
      }
    }
  }

  constructor(
    private _el: ElementRef<HTMLImageElement>,
    private renderer: Renderer2
  ) {}

  /* ================================
   * Lifecyle Hooks
   * ================================ */

  ngOnInit(): void {
    // We want to hide the img weird border or horrendous look if the image is not loaded yet
    this.renderer.setStyle(this._el.nativeElement, 'display', 'none');
  }

  ngAfterViewInit(): void {
    this.init();
  }

  /* ================================
   * Public API
   * ================================ */

  load(): void {
    this.renderer.setProperty(this._el.nativeElement, 'src', this._src);
    this.renderer.setAttribute(this._el.nativeElement, 'draggable', 'false');
    this.renderer.setStyle(this._el.nativeElement, 'display', 'block');
  }

  /* ================================
   * Private Methods
   * ================================ */

  private init(): void {
    this._src = this._contentImgPipe.transform(this.dataSrc, this.imgOptions.variantId, this.imgOptions.loyaltyType);
    const parent = this._el.nativeElement.parentElement;

    const imgPreloadObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.renderer.addClass(parent, 'skeleton'); // Add skeleton class to parent element
            this.load();
            imgPreloadObserver.disconnect();
          }
        });
      },
      { rootMargin: '100px' }
    );

    if (parent) {
      imgPreloadObserver.observe(parent);
    }
  }
}
